import Seo, { SITE, DEFAULT_OG } from "../components/Seo";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, BookOpen, Gift, ArrowRight, Printer, BookHeart, MapPin, LocateFixed, Library } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { GENERI, CONDIZIONI, REGIONI, PROVINCE_BY_REGIONE, COMUNE_INFO, COMUNE_COORDS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import BookCard from "../components/BookCard";
import MiniBooksMap from "../components/MiniBooksMap";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import DonationForm from "../components/DonationForm";
import ComuneCombobox from "../components/ComuneCombobox";
import InviteFriend from "../components/InviteFriend";

const HERO = "https://images.unsplash.com/photo-1529590003495-b2646e2718bf?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const ALL_PROVINCE = (() => {
  const seen = new Map();
  for (const r of REGIONI) for (const p of (PROVINCE_BY_REGIONE[r] || [])) if (!seen.has(p.s)) seen.set(p.s, p);
  return Array.from(seen.values()).sort((a, b) => a.n.localeCompare(b.n));
})();

const RADII = [5, 10, 25, 50, 100];

function distKm(a, b) {
  const R = 6371;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLng = (b[1] - a[1]) * Math.PI / 180;
  const la1 = a[0] * Math.PI / 180, la2 = b[0] * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function FilterFields({ genre, setGenre, comune, setComune, condition, setCondition, onlyGift, setOnlyGift, regione, setRegione, provincia, setProvincia, nearComune, radius, setRadius, onUseLocation, refReady, locating }) {
  const provOptions = regione !== "all" ? (PROVINCE_BY_REGIONE[regione] || []) : ALL_PROVINCE;
  return (
    <>
      <Select value={genre} onValueChange={setGenre}>
        <SelectTrigger data-testid="filter-genre"><SelectValue placeholder="Genere" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti i generi</SelectItem>
          {GENERI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={regione} onValueChange={(v) => { setRegione(v); setProvincia("all"); }}>
        <SelectTrigger data-testid="filter-regione"><SelectValue placeholder="Regione" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte le regioni</SelectItem>
          {REGIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={provincia} onValueChange={setProvincia}>
        <SelectTrigger data-testid="filter-provincia"><SelectValue placeholder="Provincia" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte le province</SelectItem>
          {provOptions.map((p) => <SelectItem key={p.s} value={p.s}>{p.n} ({p.s})</SelectItem>)}
        </SelectContent>
      </Select>
      <ComuneCombobox value={comune} onChange={setComune} testId="filter-comune" allowAll allLabel="Tutti i comuni" placeholder="Comune" nearComune={nearComune} triggerClassName="h-9" />
      <Select value={condition} onValueChange={setCondition}>
        <SelectTrigger data-testid="filter-condition"><SelectValue placeholder="Condizioni" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte le condizioni</SelectItem>
          {CONDIZIONI.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button data-testid="filter-gift" variant={onlyGift ? "default" : "outline"}
        onClick={() => setOnlyGift((v) => !v)}
        className={`w-full justify-center ${onlyGift ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}>
        <Gift className="w-4 h-4 mr-2" />Solo in regalo
      </Button>
      <div className="flex gap-2">
        <Select value={radius} onValueChange={setRadius}>
          <SelectTrigger data-testid="filter-radius" className="flex-1"><SelectValue placeholder="Distanza" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Qualsiasi distanza</SelectItem>
            {RADII.map((r) => <SelectItem key={r} value={String(r)}>Entro {r} km da me</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="icon" data-testid="filter-use-location"
          title="Usa la mia posizione" onClick={onUseLocation} disabled={locating}
          className={`shrink-0 ${refReady ? "text-secondary border-secondary/50" : ""}`}>
          <LocateFixed className={`w-4 h-4 ${locating ? "animate-pulse" : ""}`} />
        </Button>
      </div>
    </>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("all");
  const [comune, setComune] = useState(() => localStorage.getItem("catalog_comune") || "all");
  const [condition, setCondition] = useState("all");
  const [onlyGift, setOnlyGift] = useState(false);
  const [regione, setRegione] = useState("all");
  const [provincia, setProvincia] = useState("all");
  const [radius, setRadius] = useState("any");
  const [geo, setGeo] = useState(null);
  const [locating, setLocating] = useState(false);
  const [stats, setStats] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    api.get("/stats/community").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (comune && comune !== "all") localStorage.setItem("catalog_comune", comune);
    else localStorage.removeItem("catalog_comune");
  }, [comune]);

  const refPoint = geo || (user?.comune && COMUNE_COORDS[user.comune]) || null;

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      if (user?.comune && COMUNE_COORDS[user.comune]) { toast.info("Uso il comune del tuo profilo come riferimento."); return; }
      toast.error("Geolocalizzazione non disponibile su questo dispositivo.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo([pos.coords.latitude, pos.coords.longitude]); setLocating(false); toast.success("Posizione rilevata"); },
      () => {
        setLocating(false);
        if (user?.comune && COMUNE_COORDS[user.comune]) toast.info("Posizione negata: uso il comune del tuo profilo.");
        else toast.error("Non riesco a ottenere la posizione. Consenti l'accesso o imposta il comune nel profilo.");
      },
      { timeout: 8000 }
    );
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (genre !== "all") params.genre = genre;
      if (comune !== "all") params.comune = comune;
      if (condition !== "all") params.condition = condition;
      if (onlyGift) params.only_gift = true;
      const { data } = await api.get("/books", { params });
      setBooks(data);
    } finally { setLoading(false); }
  }, [q, genre, comune, condition, onlyGift]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const visibleBooks = books.filter((b) => {
    const info = COMUNE_INFO[b.comune];
    if (regione !== "all" && (!info || info.r !== regione)) return false;
    if (provincia !== "all" && (!info || info.s !== provincia)) return false;
    if (radius !== "any" && refPoint) {
      const c = COMUNE_COORDS[b.comune];
      if (!c || distKm(refPoint, c) > Number(radius)) return false;
    }
    return true;
  });

  const reset = () => { setQ(""); setGenre("all"); setComune("all"); setCondition("all"); setOnlyGift(false); setRegione("all"); setProvincia("all"); setRadius("any"); };

  const activeCount = (genre !== "all" ? 1 : 0) + (comune !== "all" ? 1 : 0) + (condition !== "all" ? 1 : 0) + (onlyGift ? 1 : 0) + (regione !== "all" ? 1 : 0) + (provincia !== "all" ? 1 : 0) + (radius !== "any" ? 1 : 0);

  const handleRadius = (v) => { setRadius(v); if (v !== "any" && !refPoint) requestLocation(); };

  const filterProps = { genre, setGenre, comune, setComune, condition, setCondition, onlyGift, setOnlyGift, regione, setRegione, provincia, setProvincia, nearComune: user?.comune || "", radius, setRadius: handleRadius, onUseLocation: requestLocation, refReady: !!refPoint, locating };

  return (
    <div>
      <Seo
        title="Libri in Circolo – Scambia e regala libri usati vicino a te"
        description="Dona o scambia gratis i libri che non leggi più con chi abita vicino a te. Nessuna spedizione: cerca nel catalogo, trova i libri sulla mappa e incontrati in zona."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Libri in Circolo",
            alternateName: "Scambiamocelo",
            url: SITE,
            logo: DEFAULT_OG,
            founder: { "@type": "Person", name: "Marco Paparelli" },
            vatID: "IT05042811009",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Viale Regina Margherita, 46",
              postalCode: "00198",
              addressLocality: "Roma",
              addressCountry: "IT",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Libri in Circolo",
            url: SITE,
            inLanguage: "it-IT",
          },
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-4">
              <span className="w-8 h-px bg-primary" /> Casa tua e dintorni
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.05]">
              I libri che ami,<br />di nuovo in circolo.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">
              Dona o scambia gratuitamente i libri che non leggi più con altri cittadini della tua zona. Nessun costo, nessuna spedizione: solo lettura che passa di mano in mano.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90" data-testid="hero-pubblica">
                  <Link to="/pubblica">Pubblica un libro <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              ) : (
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90" data-testid="hero-registrati">
                  <Link to="/accedi">Inizia ora <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              )}
              <Button asChild size="lg" variant="outline" data-testid="hero-esplora">
                <a href="#catalogo">Esplora il catalogo</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Dona i tuoi libri */}
      <section id="dona" className="border-b border-border bg-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-secondary mb-4">
              <BookHeart className="w-4 h-4" /> Dona i tuoi libri
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight leading-tight">Regala una seconda vita ai libri che non leggi più</h2>
            <p className="mt-4 text-muted-foreground">
              Puoi donare i tuoi libri alla biblioteca di scambiamoceli.it: verranno rimessi in circolo per raggiungere altri lettori della zona. Bastano pochi secondi — il bibliotecario ti contatterà per la consegna.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-secondary" />Nessun costo, nessuna spedizione</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-secondary" />I libri restano nella comunità</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-secondary" />Solo libri cartacei, niente contenuti digitali</li>
            </ul>
          </div>
          <DonationForm />
        </div>
      </section>

      {/* Catalogo */}
      <section id="catalogo" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold tracking-tight">Catalogo dei libri</h2>
            <p className="text-muted-foreground text-sm mt-1">Trova il tuo prossimo libro tra le offerte della comunità.</p>
            {stats && stats.books > 0 && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-secondary bg-secondary/10 border border-secondary/30 rounded-full px-3.5 py-1.5" data-testid="community-counter">
                <Library className="w-4 h-4" />
                <span><span className="font-bold text-foreground">{stats.books}</span> libri disponibili in <span className="font-bold text-foreground">{stats.comuni}</span> {stats.comuni === 1 ? "comune" : "comuni"} d'Italia</span>
              </p>
            )}
          </div>
          <Button asChild variant="outline" size="sm" data-testid="home-bacheca" className="shrink-0">
            <Link to="/bacheca"><Printer className="w-4 h-4 mr-2" />Bacheca stampabile</Link>
          </Button>
        </div>

        {/* Filtri */}
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per titolo o autore…" className="pl-10 h-11" />
          </div>
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-3">
            <FilterFields {...filterProps} />
          </div>

          <div className="flex sm:hidden items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" data-testid="mobile-filters-button" className="flex-1 justify-center">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />Filtri
                  {activeCount > 0 && <span className="ml-2 bg-primary text-primary-foreground rounded-full text-[11px] font-semibold px-1.5">{activeCount}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="mobile-filters-sheet">
                <SheetHeader><SheetTitle className="font-serif text-left">Filtra il catalogo</SheetTitle><SheetDescription className="text-left">Scegli genere, comune e condizioni per affinare i risultati.</SheetDescription></SheetHeader>
                <div className="grid grid-cols-1 gap-3 py-5">
                  <FilterFields {...filterProps} />
                </div>
                <SheetFooter className="flex-row gap-2">
                  <Button variant="outline" onClick={reset} data-testid="mobile-filters-reset" className="flex-1">Azzera</Button>
                  <SheetClose asChild>
                    <Button data-testid="mobile-filters-apply" className="flex-1 bg-primary hover:bg-primary/90">Mostra {visibleBooks.length} libri</Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />{visibleBooks.length} libri trovati</span>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="filter-reset">Azzera filtri</Button>
          </div>
        </div>

        {user?.comune && COMUNE_COORDS[user.comune] && comune !== user.comune && (
          <button
            type="button"
            data-testid="show-my-zone"
            onClick={() => { setComune(user.comune); setRegione("all"); setProvincia("all"); }}
            className="w-full sm:w-auto inline-flex items-center gap-2 mb-6 px-4 h-10 rounded-full text-sm font-medium border border-secondary/40 bg-secondary/10 text-secondary hover:bg-secondary/20 transition-colors"
          >
            <MapPin className="w-4 h-4" />Mostra prima i libri di {user.comune}
          </button>
        )}

        {!loading && <MiniBooksMap books={visibleBooks} onExpand={() => setMapOpen(true)} className="mb-8" />}

        <Dialog open={mapOpen} onOpenChange={setMapOpen}>
          <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 z-[1200]" data-testid="fullscreen-map-dialog">
            <DialogHeader className="px-5 py-3 border-b border-border">
              <DialogTitle className="font-serif flex items-center gap-2"><MapPin className="w-5 h-5 text-secondary" />Mappa dei libri</DialogTitle>
            </DialogHeader>
            <div className="p-3">
              {mapOpen && <MiniBooksMap books={visibleBooks} height="72vh" />}
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}
          </div>
        ) : visibleBooks.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-dashed border-border">
            <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-serif text-lg">Nessun libro trovato</p>
            <p className="text-sm text-muted-foreground mt-1">Prova a modificare i filtri o pubblica il primo libro.</p>
          </div>
        ) : (
          <div data-testid="books-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visibleBooks.map((b) => <BookCard key={b.id} book={b} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="invite-section">
        <div className="relative overflow-hidden rounded-3xl bg-secondary text-secondary-foreground px-6 py-10 sm:px-12 sm:py-14">
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80 mb-3">Fai girare la voce</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Invita un amico a Libri in Circolo</h2>
              <p className="mt-3 text-sm sm:text-base opacity-90">Più siamo, più libri tornano a circolare. Condividi il progetto con chi ama leggere: bastano pochi secondi.</p>
            </div>
            <InviteFriend variant="secondary" size="lg" align="end" label="Invita un amico" className="shrink-0 bg-background text-secondary hover:bg-background/90 shadow-lg h-12 px-6 text-base" />
          </div>
          <BookHeart className="absolute -right-6 -bottom-6 w-40 h-40 opacity-10 rotate-12" />
        </div>
      </section>
    </div>
  );
}
